/**
 * ReconciliationTab (M5) — physical vs system inventory via scan-based
 * cycle counts. Discrepancies auto-flagged → categorise → threshold-based
 * approval → ledger adjustment posted back to mciEntries.receipts[].
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, Plus, X, Check, RefreshCw, Loader2, Sparkles,
         AlertTriangle, TrendingUp, Package, Shield, Siren, FileText,
         ClipboardCopy, ChevronRight, Wrench } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const CATEGORIES = ['PILFERAGE', 'WASTAGE_INSTALLATION', 'WASTAGE_HANDLING',
                     'DAMAGED_UNREPORTED', 'WRONG_BIN', 'SYSTEM_ERROR', 'SCAN_MISSED'];
const CADENCES = ['DAILY_CYCLE', 'WEEKLY_CYCLE', 'MONTHLY_FULL', 'ADHOC'];

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (iso) => (iso || '—').toString().slice(0, 10);

function KpiTile({ label, value, sub, color, testId }) {
  return (
    <div className="p-2.5 rounded-lg border bg-white" style={{ borderColor: C.border }}
      data-testid={testId}>
      <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>{label}</div>
      <div className="text-lg font-bold font-mono mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[9px] font-mono" style={{ color: C.slateMuted }}>{sub}</div>}
    </div>
  );
}

function WorkspaceDialog({ projectId, warehouseLocation, onClose, onSaved }) {
  const [reconId, setReconId] = useState(null);
  const [autoCountList, setAutoCountList] = useState([]);
  const [autoCountLoading, setAutoCountLoading] = useState(true);
  const [mci, setMci] = useState('');
  const [mciMeta, setMciMeta] = useState(null);   // discipline, vendor, PO, description
  const [mciSearch, setMciSearch] = useState('');
  const [mciResults, setMciResults] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitId, setUnitId] = useState('');
  const [expected, setExpected] = useState('');
  const [counted, setCounted] = useState('');
  const [preCategory, setPreCategory] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState([]);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
    catch { return {}; }
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/v1/projects/${projectId}/mtx/reconciliations`,
          { warehouseLocation, cadence: 'ADHOC' });
        setReconId(data.id);
      } catch { toast.error('Could not start reconciliation'); }
    })();
  }, [projectId, warehouseLocation]);

  // Auto-count list — every material at this warehouse (top 30)
  useEffect(() => {
    let alive = true;
    (async () => {
      setAutoCountLoading(true);
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(warehouseLocation)}/material-analysis`);
        if (alive) setAutoCountList((data.materialCodes || []).slice(0, 60));
      } catch { setAutoCountList([]); }
      setAutoCountLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, warehouseLocation]);

  // MCI live search
  useEffect(() => {
    if (mciSearch.length < 2) { setMciResults([]); return; }
    const t = setTimeout(() => {
      const q = mciSearch.toLowerCase();
      setMciResults(autoCountList.filter(r =>
        r.mciLineNumber?.toLowerCase().includes(q)
        || r.description?.toLowerCase().includes(q)
        || r.materialCode?.toLowerCase().includes(q)
        || (r.vendor || '').toLowerCase().includes(q)
      ).slice(0, 12));
    }, 200);
    return () => clearTimeout(t);
  }, [mciSearch, autoCountList]);

  const pickMci = async (r) => {
    setMci(r.mciLineNumber); setMciSearch(''); setMciResults([]);
    setExpected(String(r.finalInventory || r.iqcAccepted || 0));
    setMciMeta({
      description: r.description, discipline: r.discipline, vendor: r.vendor,
      poNumber: r.poNumber, unit: r.unit, currency: r.currency,
      unitValue: r.unitValue,
    });
    // Fetch unit options for this MCI at this warehouse
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mtx/material-units?mci=${encodeURIComponent(r.mciLineNumber)}&limit=100`);
      const rows = (data.units || []).filter(u =>
        !warehouseLocation
        || (u.currentBin || u.warehouseLocation || '') === warehouseLocation);
      setUnitOptions(rows);
    } catch { setUnitOptions([]); }
  };
  const clearMci = () => {
    setMci(''); setMciMeta(null); setExpected(''); setUnitId('');
    setUnitOptions([]); setPreCategory(''); setRootCause('');
  };

  const pickUnit = (uid) => {
    setUnitId(uid);
    const u = unitOptions.find(x => x.id === uid || x.materialUnitId === uid);
    if (u && u.quantity != null) setExpected(String(u.quantity));
  };

  const record = async () => {
    if (!reconId || !parseFloat(expected)) return toast.error('Expected qty required');
    setBusy(true);
    try {
      const { data } = await api.patch(`/v1/projects/${projectId}/mtx/reconciliations/${reconId}/count`, {
        mciLineNumber: mci || null, materialUnitId: unitId || null,
        expectedQty: parseFloat(expected), countedQty: parseFloat(counted || 0),
      });
      // Auto-categorise if pre-selected
      if (data.discrepancyId && preCategory) {
        try {
          await api.patch(`/v1/projects/${projectId}/mtx/discrepancies/${data.discrepancyId}/categorise`,
            { category: preCategory, rootCause });
        } catch { /* silent */ }
      }
      setEntries([{ ...data.entry, discrepancyId: data.discrepancyId,
                     preCategory: data.discrepancyId ? preCategory : null }, ...entries]);
      setExpected(''); setCounted(''); clearMci();
      if (data.discrepancyId) toast.warning(`Discrepancy auto-flagged: ${data.discrepancyId}${preCategory ? ' · '+preCategory : ''}`);
      else toast.success('No variance');
    } catch { toast.error('Count entry failed'); }
    setBusy(false);
  };

  const inp = "w-full px-2 py-1 rounded border text-[11px]";
  const varianceLive = (parseFloat(counted || 0) - parseFloat(expected || 0));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="recon-workspace">
      <div className="bg-white rounded-xl w-full max-w-5xl shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderColor: C.border }}>
          <div>
            <div className="text-sm font-bold" style={{ color: C.slate }}>Reconciliation Workspace</div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              {warehouseLocation} · {reconId || '…'} · Counter: <b>{user.name || user.email || 'current user'}</b> · {new Date().toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} data-testid="recon-ws-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 grid grid-cols-12 gap-3">
          {/* Left: Auto Count List (real materials) */}
          <div className="col-span-4 rounded border p-2" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase font-bold mb-1 flex items-center justify-between" style={{ color: C.slateMuted }}>
              <span>Auto Count List</span>
              <span className="text-[9px]" style={{ color: C.slateMuted }}>{autoCountList.length} SKUs</span>
            </div>
            {autoCountLoading && <div className="text-[9.5px] italic" style={{ color: C.slateMuted }}>Loading…</div>}
            <div className="max-h-96 overflow-y-auto space-y-1">
              {autoCountList.map((r, i) => {
                const active = mci === r.mciLineNumber;
                return (
                  <button key={i} onClick={() => pickMci(r)}
                    className="block w-full text-left px-1.5 py-1 rounded border text-[10px]"
                    style={{ borderColor: active ? C.primary : C.border,
                              background: active ? '#EFF6FF' : '#fff' }}
                    data-testid={`recon-auto-${i}`}>
                    <div className="flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" style={{ color: C.slateMuted }} />
                      <span className="font-mono font-bold" style={{ color: C.primary }}>{r.mciLineNumber}</span>
                      <span className="ml-auto font-mono" style={{ color: C.success }}>{r.finalInventory ?? r.iqcAccepted}</span>
                    </div>
                    <div className="truncate text-[9.5px]" style={{ color: C.slate }}>{r.description}</div>
                    <div className="text-[8.5px] font-mono" style={{ color: C.slateMuted }}>
                      {r.discipline || '—'} · {r.vendor?.slice(0, 24) || '—'}
                    </div>
                  </button>
                );
              })}
              {!autoCountLoading && autoCountList.length === 0 && (
                <div className="text-[10px] italic" style={{ color: C.slateMuted }}>No inventory at this location.</div>
              )}
            </div>
          </div>

          {/* Center: Record Count with autofills */}
          <div className="col-span-4 rounded border p-2 space-y-1.5 relative" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase font-bold" style={{ color: C.slateMuted }}>Record Count</div>
            {mci ? (
              <div className="p-1.5 rounded border" style={{ background: '#F0FDF4', borderColor: C.success }}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold" style={{ color: C.primary }}>{mci}</span>
                  <button onClick={clearMci} className="text-[9px] underline" style={{ color: C.slateMuted }}
                    data-testid="recon-mci-change">change</button>
                </div>
                {mciMeta && (
                  <div className="text-[9px] mt-0.5 space-y-0.5" style={{ color: C.slate }}>
                    <div className="truncate">{mciMeta.description}</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {mciMeta.discipline && <span className="px-1 rounded font-mono" style={{ background: '#E0E7FF', color: C.primary }}>{mciMeta.discipline}</span>}
                      {mciMeta.vendor && <span className="px-1 rounded font-mono" style={{ background: '#FEF3C7', color: '#92400E' }}>{mciMeta.vendor}</span>}
                      {mciMeta.poNumber && <span className="px-1 rounded font-mono" style={{ background: '#F1F5F9', color: C.slateMuted }}>PO {mciMeta.poNumber}</span>}
                    </div>
                    {mciMeta.unitValue != null && (
                      <div className="font-mono text-[8.5px]" style={{ color: C.slateMuted }}>
                        Unit value: {mciMeta.currency || 'USD'} {Number(mciMeta.unitValue).toFixed(2)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <input value={mciSearch} onChange={e => setMciSearch(e.target.value)}
                placeholder="Search MCI (code / desc / vendor / material)"
                className={inp} style={{ borderColor: C.border }} data-testid="recon-mci" />
            )}
            {mciResults.length > 0 && (
              <div className="absolute top-16 left-2 right-2 bg-white border rounded shadow-lg z-30 max-h-40 overflow-y-auto"
                style={{ borderColor: C.border }}>
                {mciResults.map((r, i) => (
                  <button key={i} onClick={() => pickMci(r)}
                    className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50"
                    style={{ borderBottom: `1px solid ${C.border}` }}
                    data-testid={`recon-mci-option-${i}`}>
                    <div className="font-mono font-bold" style={{ color: C.primary }}>{r.mciLineNumber}</div>
                    <div className="truncate" style={{ color: C.slate }}>{r.description}</div>
                    <div style={{ color: C.slateMuted }}>Expected: <b>{r.finalInventory}</b> · {r.discipline}</div>
                  </button>
                ))}
              </div>
            )}
            {/* Material Unit picker */}
            {mci && unitOptions.length > 0 ? (
              <select value={unitId} onChange={e => pickUnit(e.target.value)}
                className={inp} style={{ borderColor: C.border }} data-testid="recon-unit-picker">
                <option value="">— All units (aggregate) —</option>
                {unitOptions.map(u => (
                  <option key={u.id || u.materialUnitId} value={u.id || u.materialUnitId}>
                    {u.id || u.materialUnitId} · qty {u.quantity} · {u.currentState || 'IN_STOCK'}
                  </option>
                ))}
              </select>
            ) : (
              <input value={unitId} onChange={e => setUnitId(e.target.value)}
                placeholder={mci ? 'Material Unit ID (optional)' : 'Pick an MCI first'}
                className={inp} disabled={!mci}
                style={{ borderColor: C.border }} data-testid="recon-unit" />
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>Expected</div>
                <input type="number" value={expected} onChange={e => setExpected(e.target.value)}
                  placeholder="autofilled" className={inp} style={{ borderColor: C.border }}
                  data-testid="recon-expected" />
              </div>
              <div>
                <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>Counted</div>
                <input type="number" value={counted} onChange={e => setCounted(e.target.value)}
                  placeholder="physical count" className={inp} style={{ borderColor: C.border }}
                  data-testid="recon-counted" />
              </div>
            </div>
            {counted !== '' && expected !== '' && (
              <div className="text-[10px] p-1 rounded font-mono"
                style={{ background: varianceLive === 0 ? '#F0FDF4' : '#FEF3C7',
                          color: varianceLive === 0 ? C.success : C.warn }}>
                Variance: <b>{varianceLive.toFixed(2)}</b>
                {mciMeta?.unitValue != null && varianceLive !== 0 && (
                  <span> · Impact: <b>{mciMeta.currency || 'USD'} {(mciMeta.unitValue * varianceLive).toFixed(2)}</b></span>
                )}
              </div>
            )}
            {/* Pre-category (populated only when variance ≠ 0) */}
            <div>
              <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>Pre-categorise (optional)</div>
              <select value={preCategory} onChange={e => setPreCategory(e.target.value)}
                className={inp} style={{ borderColor: C.border }} data-testid="recon-precat">
                <option value="">— assign later —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
              {preCategory && (
                <input value={rootCause} onChange={e => setRootCause(e.target.value)}
                  placeholder="Root cause (optional)"
                  className={inp + ' mt-1'} style={{ borderColor: C.border }}
                  data-testid="recon-rootcause" />
              )}
            </div>
            <Button size="sm" onClick={record} disabled={busy || !reconId}
              className="text-white w-full" style={{ background: C.accent }}
              data-testid="recon-submit-count">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Record + Variance Check
            </Button>
          </div>

          {/* Right: entries log */}
          <div className="col-span-4 rounded border p-2" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>Entries ({entries.length})</div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {entries.length === 0 && <div className="text-[10px]" style={{ color: C.slateMuted }}>Nothing recorded yet.</div>}
              {entries.map((e, i) => (
                <div key={i} className="p-1.5 rounded text-[9.5px]"
                  style={{ background: e.variance !== 0 ? '#FEF3C7' : '#F0FDF4' }}>
                  <div className="font-mono font-bold" style={{ color: C.slate }}>{e.mciLineNumber || 'no MCI'}</div>
                  <div>Exp <b>{e.expectedQty}</b> · Counted <b>{e.countedQty}</b> · Var <b style={{ color: e.variance !== 0 ? C.danger : C.success }}>{e.variance}</b></div>
                  {e.discrepancyId && (
                    <div className="text-[9px] font-mono flex items-center gap-1" style={{ color: C.accent }}>
                      DSC {e.discrepancyId}
                      {e.preCategory && <span className="px-1 rounded" style={{ background: '#E0E7FF', color: C.primary }}>{e.preCategory}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-3 border-t flex justify-end" style={{ borderColor: C.border }}>
          <Button size="sm" onClick={() => { onSaved?.(); onClose(); }}
            className="text-white" style={{ background: C.primary }}>Close Session</Button>
        </div>
      </div>
    </div>
  );
}


function CaseDraftModal({ projectId, disc, kind, existingCase, onClose, onSaved }) {
  // kind: 'INSURANCE' | 'THEFT'
  const [narrative, setNarrative] = useState(existingCase?.narrative || '');
  const [loading, setLoading] = useState(!existingCase);
  const [busy, setBusy] = useState(false);
  const [claimId, setClaimId] = useState(existingCase?.id || null);
  const [extra, setExtra] = useState(existingCase?.additionalContext || '');
  const [claimAmount, setClaimAmount] = useState(existingCase?.requestedAmount || (disc.valueImpact ? Math.abs(disc.valueImpact) : ''));
  const [insurerName, setInsurerName] = useState(existingCase?.insurerName || '');
  const [policyNumber, setPolicyNumber] = useState(existingCase?.policyNumber || '');
  const [suspects, setSuspects] = useState((existingCase?.suspects || []).join(', '));
  const [cctv, setCctv] = useState((existingCase?.cctvClips || []).join(', '));
  const [filePolice, setFilePolice] = useState(existingCase?.fileWithPolice ?? (kind === 'THEFT'));

  const draft = useCallback(async () => {
    setLoading(true);
    try {
      const url = kind === 'INSURANCE'
        ? `/v1/projects/${projectId}/mtx/discrepancies/${disc.id}/insurance-claim`
        : `/v1/projects/${projectId}/mtx/discrepancies/${disc.id}/theft-report`;
      const body = kind === 'INSURANCE'
        ? { additionalContext: extra || null, claimAmount: claimAmount ? Number(claimAmount) : null,
            insurerName: insurerName || null, policyNumber: policyNumber || null }
        : { additionalContext: extra || null,
            suspects: suspects.split(',').map(s => s.trim()).filter(Boolean),
            cctvClips: cctv.split(',').map(s => s.trim()).filter(Boolean),
            fileWithPolice: filePolice };
      const { data } = await api.post(url, body);
      setNarrative(data.narrative || '');
      setClaimId(data.id);
      toast.success(`${kind === 'INSURANCE' ? 'Insurance claim' : 'Theft report'} drafted by Cognara`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cognara draft failed'); }
    setLoading(false);
  }, [projectId, disc.id, kind, extra, claimAmount, insurerName, policyNumber, suspects, cctv, filePolice]);

  useEffect(() => {
    if (!existingCase) draft(); /* auto-draft on open unless viewing existing */ // eslint-disable-next-line
  }, []);

  const save = async () => {
    if (!claimId) return;
    setBusy(true);
    try {
      const url = kind === 'INSURANCE'
        ? `/v1/projects/${projectId}/mtx/insurance-claims/${claimId}`
        : `/v1/projects/${projectId}/mtx/theft-reports/${claimId}`;
      await api.patch(url, { narrative, status: 'SUBMITTED' });
      toast.success('Case saved');
      onSaved?.(); onClose();
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(narrative); toast.success('Copied to clipboard'); }
    catch { toast.error('Copy failed'); }
  };

  const isIns = kind === 'INSURANCE';
  const themeColor = isIns ? '#1E4E8C' : '#DC2626';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}
      data-testid={isIns ? 'insurance-claim-modal' : 'theft-report-modal'}>
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-white z-10"
          style={{ borderColor: C.border, background: `linear-gradient(90deg, ${themeColor}11 0%, #fff 100%)` }}>
          <div className="flex items-center gap-2">
            {isIns ? <Shield className="w-4 h-4" style={{ color: themeColor }} />
                    : <Siren className="w-4 h-4" style={{ color: themeColor }} />}
            <div>
              <div className="text-sm font-bold" style={{ color: themeColor }}>
                {isIns ? 'Insurance Claim' : 'Theft / Pilferage Report'} · Cognara draft
              </div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>
                DSC {disc.id} · {disc.mciLineNumber || 'no MCI'} · Impact {disc.currency} {fmt(Math.abs(disc.valueImpact || 0))}
              </div>
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 grid grid-cols-3 gap-3">
          <div className="col-span-1 space-y-1.5">
            <div className="text-[10px] uppercase font-bold" style={{ color: C.slateMuted }}>
              {isIns ? 'Claim Inputs' : 'Report Inputs'}
            </div>
            {isIns && (
              <>
                <label className="block text-[9px] font-semibold" style={{ color: C.slateMuted }}>Insurer name</label>
                <input value={insurerName} onChange={e => setInsurerName(e.target.value)}
                  placeholder="ADNIC / Oriental / …"
                  className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}
                  data-testid="case-insurer" />
                <label className="block text-[9px] font-semibold" style={{ color: C.slateMuted }}>Policy number</label>
                <input value={policyNumber} onChange={e => setPolicyNumber(e.target.value)}
                  placeholder="CAR-2026-…" className="w-full px-2 py-1 rounded border text-[11px]"
                  style={{ borderColor: C.border }} data-testid="case-policy" />
                <label className="block text-[9px] font-semibold" style={{ color: C.slateMuted }}>Claim amount ({disc.currency})</label>
                <input value={claimAmount} onChange={e => setClaimAmount(e.target.value)} type="number"
                  className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}
                  data-testid="case-amount" />
              </>
            )}
            {!isIns && (
              <>
                <label className="block text-[9px] font-semibold" style={{ color: C.slateMuted }}>Suspects (comma-sep)</label>
                <input value={suspects} onChange={e => setSuspects(e.target.value)}
                  placeholder="employee1@…, contractor2@…"
                  className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}
                  data-testid="case-suspects" />
                <label className="block text-[9px] font-semibold" style={{ color: C.slateMuted }}>CCTV clip URLs (comma-sep)</label>
                <input value={cctv} onChange={e => setCctv(e.target.value)}
                  placeholder="https://…/gate-3.mp4, …"
                  className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}
                  data-testid="case-cctv" />
                <label className="flex items-center gap-1 text-[10px] mt-1" style={{ color: C.slate }}>
                  <input type="checkbox" checked={filePolice} onChange={e => setFilePolice(e.target.checked)}
                    data-testid="case-file-police" />
                  File with police (FIR)
                </label>
              </>
            )}
            <label className="block text-[9px] font-semibold mt-2" style={{ color: C.slateMuted }}>Additional context</label>
            <textarea value={extra} onChange={e => setExtra(e.target.value)}
              rows={4} placeholder="Anything Cognara should include…"
              className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}
              data-testid="case-extra" />
            <Button size="sm" onClick={draft} disabled={loading}
              className="text-white w-full mt-1" style={{ background: themeColor }}
              data-testid="case-redraft">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Re-draft with Cognara
            </Button>
          </div>
          <div className="col-span-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase font-bold" style={{ color: C.slateMuted }}>Cognara Narrative · editable</div>
              <button onClick={copy} disabled={!narrative}
                className="text-[9px] px-1.5 py-0.5 rounded border flex items-center gap-1"
                style={{ borderColor: C.border, color: C.slate }}
                data-testid="case-copy">
                <ClipboardCopy className="w-3 h-3" /> Copy
              </button>
            </div>
            {loading ? (
              <div className="h-96 flex items-center justify-center text-[10px]" style={{ color: C.slateMuted }}>
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cognara is drafting…
              </div>
            ) : (
              <textarea value={narrative} onChange={e => setNarrative(e.target.value)}
                rows={22} className="w-full px-2 py-1 rounded border text-[11px] font-mono"
                style={{ borderColor: C.border }} data-testid="case-narrative" />
            )}
          </div>
        </div>
        <div className="p-3 border-t flex justify-between items-center" style={{ borderColor: C.border }}>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>
            {claimId ? `Draft ID: ${claimId}` : 'Not saved yet'}
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" onClick={onClose}
              data-testid="case-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || !narrative}
              className="text-white" style={{ background: C.success }}
              data-testid="case-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Save & submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function CasesPanel({ projectId, refreshKey, onOpenCase }) {
  const [claims, setClaims] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [c, r] = await Promise.all([
          api.get(`/v1/projects/${projectId}/mtx/insurance-claims`),
          api.get(`/v1/projects/${projectId}/mtx/theft-reports`),
        ]);
        if (!alive) return;
        setClaims(c.data.claims || []); setReports(r.data.reports || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  if (loading) return null;
  if (claims.length === 0 && reports.length === 0) return null;
  return (
    <div className="rounded-lg border" style={{ borderColor: C.border }} data-testid="cases-panel">
      <div className="px-2 py-1.5 border-b flex items-center gap-1.5" style={{ borderColor: C.border }}>
        <FileText className="w-3.5 h-3.5" style={{ color: C.primary }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.primary }}>Insurance & Theft Cases</div>
        <span className="ml-auto text-[10px] font-mono">{claims.length + reports.length} cases</span>
      </div>
      <div className="grid grid-cols-2 gap-0">
        <div className="p-2 border-r" style={{ borderColor: C.border }}>
          <div className="text-[9.5px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ color: '#1E4E8C' }}>
            <Shield className="w-3 h-3" /> Insurance Claims ({claims.length})
          </div>
          {claims.length === 0 && <div className="text-[10px] italic" style={{ color: C.slateMuted }}>None yet.</div>}
          {claims.map(c => (
            <button key={c.id} onClick={() => onOpenCase?.(c, 'INSURANCE')}
              className="w-full text-left text-[10px] p-1 rounded border mb-1 hover:shadow-sm transition-all"
              style={{ borderColor: C.border, background: '#F8FAFC' }}
              data-testid={`case-claim-${c.id}`}>
              <div className="font-mono font-bold" style={{ color: '#1E4E8C' }}>{c.id}</div>
              <div>{c.mciLineNumber || '—'} · {c.currency} {fmt(Math.abs(c.requestedAmount || 0))}</div>
              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                {c.insurerName || '—'} · {c.policyNumber || '—'} · <b>{c.status}</b>
              </div>
            </button>
          ))}
        </div>
        <div className="p-2">
          <div className="text-[9.5px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ color: '#DC2626' }}>
            <Siren className="w-3 h-3" /> Theft Reports ({reports.length})
          </div>
          {reports.length === 0 && <div className="text-[10px] italic" style={{ color: C.slateMuted }}>None yet.</div>}
          {reports.map(r => (
            <button key={r.id} onClick={() => onOpenCase?.(r, 'THEFT')}
              className="w-full text-left text-[10px] p-1 rounded border mb-1 hover:shadow-sm transition-all"
              style={{ borderColor: C.border, background: '#FEF2F2' }}
              data-testid={`case-report-${r.id}`}>
              <div className="font-mono font-bold" style={{ color: '#DC2626' }}>{r.id}</div>
              <div>{r.mciLineNumber || '—'} · {r.currency} {fmt(Math.abs(r.valueImpact || 0))}</div>
              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                {(r.suspects || []).length} suspect(s) · {(r.cctvClips || []).length} CCTV clip(s) · <b>{r.status}</b>
                {r.fileWithPolice && <span className="ml-1 px-1 rounded font-bold" style={{ background: '#FEE2E2', color: '#991B1B' }}>FIR</span>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function DiscrepancyRow({ projectId, disc, onChanged, onOpenCase }) {
  const [cat, setCat] = useState(disc.category || '');
  const [rootCause, setRootCause] = useState(disc.rootCause || '');
  const [busy, setBusy] = useState(false);

  const categorise = async () => {
    if (!cat) return toast.error('Pick a category');
    setBusy(true);
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/discrepancies/${disc.id}/categorise`,
        { category: cat, rootCause });
      toast.success(`Categorised · Approver: ${cat === 'PILFERAGE' ? 'PROJECT_DIRECTOR + Security notified' : 'route by value'}`);
      onChanged?.();
    } catch { toast.error('Categorise failed'); }
    setBusy(false);
  };
  const approve = async () => {
    setBusy(true);
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/discrepancies/${disc.id}/approve`);
      toast.success('Approved · ledger adjusted');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    setBusy(false);
  };

  const insuranceEligible = disc.category && disc.category !== 'SYSTEM_ERROR' && disc.category !== 'WRONG_BIN' && (disc.valueImpact || 0) !== 0;
  const theftEligible = disc.category === 'PILFERAGE' || disc.category === 'DAMAGED_UNREPORTED';

  return (
    <tr style={{ borderTop: `1px solid ${C.border}` }}
      data-testid={`disc-row-${disc.id}`}>
      <td className="p-1.5 text-[10px]" style={{ color: C.slate }}>{disc.mciLineNumber || '—'}</td>
      <td className="p-1.5 text-[10px] font-mono" style={{ color: C.primary }}>{disc.materialUnitId || '—'}</td>
      <td className="p-1.5 text-[10px] font-mono">{disc.expectedQty}</td>
      <td className="p-1.5 text-[10px] font-mono">{disc.countedQty}</td>
      <td className="p-1.5 text-[10px] font-mono" style={{ color: disc.variance < 0 ? C.danger : C.success }}>{disc.variance}</td>
      <td className="p-1.5 text-[10px] font-mono">{disc.currency} {fmt(Math.abs(disc.valueImpact))}</td>
      <td className="p-1.5">
        <select value={cat} onChange={e => setCat(e.target.value)}
          className="w-full text-[9px] px-1 py-0.5 rounded border" style={{ borderColor: C.border }}
          data-testid={`disc-cat-${disc.id}`}>
          <option value="">— category —</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
      </td>
      <td className="p-1.5 text-[9px]">
        <span className="px-1 rounded font-bold" style={{ background: '#EEF2FF', color: C.primary }}>{disc.approvalRole}</span>
      </td>
      <td className="p-1.5">
        <span className="text-[9px] px-1 rounded font-bold text-white"
          style={{ background: disc.status === 'APPROVED' ? C.success : C.warn }}>{disc.status}</span>
      </td>
      <td className="p-1.5 flex gap-1 flex-wrap">
        {!disc.category && (
          <button onClick={categorise} disabled={busy || !cat}
            className="text-[9px] px-2 py-0.5 rounded text-white" style={{ background: C.accent }}
            data-testid={`disc-categorise-${disc.id}`}>Categorise</button>
        )}
        {disc.category && disc.status === 'OPEN' && (
          <button onClick={approve} disabled={busy}
            className="text-[9px] px-2 py-0.5 rounded text-white" style={{ background: C.success }}
            data-testid={`disc-approve-${disc.id}`}>Approve</button>
        )}
        {insuranceEligible && (
          <button onClick={() => onOpenCase?.(disc, 'INSURANCE')}
            className="text-[9px] px-1.5 py-0.5 rounded text-white flex items-center gap-0.5"
            style={{ background: '#1E4E8C' }}
            title={disc.insuranceClaimId ? 'Claim drafted · reopen' : 'Draft insurance claim with Cognara'}
            data-testid={`disc-insurance-${disc.id}`}>
            <Shield className="w-2.5 h-2.5" />{disc.insuranceClaimId ? 'Claim ✓' : 'Insurance'}
          </button>
        )}
        {theftEligible && (
          <button onClick={() => onOpenCase?.(disc, 'THEFT')}
            className="text-[9px] px-1.5 py-0.5 rounded text-white flex items-center gap-0.5"
            style={{ background: '#DC2626' }}
            title={disc.theftReportId ? 'Report drafted · reopen' : 'Draft theft/pilferage report with Cognara'}
            data-testid={`disc-theft-${disc.id}`}>
            <Siren className="w-2.5 h-2.5" />{disc.theftReportId ? 'Report ✓' : 'Theft'}
          </button>
        )}
      </td>
    </tr>
  );
}

function CognaraPanel({ projectId, refreshKey }) {
  const [ins, setIns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/reconciliation/cognara-insights`);
        if (alive) setIns(data.insights || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  const sev = { HIGH: C.danger, MEDIUM: C.warn, INFO: C.info };
  return (
    <div className="sticky top-4 w-64 flex-shrink-0 rounded-lg border p-2"
      style={{ borderColor: C.border, background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)' }}
      data-testid="cognara-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Cognara · Reconciliation</div>
      </div>
      {loading && <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>Analyzing…</div>}
      {!loading && ins.map((i, idx) => (
        <div key={idx} className="mb-2 p-2 rounded border bg-white" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-1 mb-1">
            <span className="px-1 rounded text-[8.5px] font-bold text-white" style={{ background: sev[i.severity] || C.slateMuted }}>{i.severity}</span>
            <div className="text-[10px] font-bold" style={{ color: C.slate }}>{i.title}</div>
          </div>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>{i.detail}</div>
          <div className="text-[9px] mt-1 italic" style={{ color: C.primary }}>→ {i.action}</div>
        </div>
      ))}
    </div>
  );
}

export default function ReconciliationTab({ projectId }) {
  const [kpis, setKpis] = useState({});
  const [board, setBoard] = useState([]);
  const [discs, setDiscs] = useState([]);
  const [openWorkspace, setOpenWorkspace] = useState(null);
  const [caseModal, setCaseModal] = useState(null); // {disc, kind, existingCase?}
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [k, b, d] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/reconciliation/kpis`),
        api.get(`/v1/projects/${projectId}/mtx/reconciliation/board`),
        api.get(`/v1/projects/${projectId}/mtx/discrepancies`),
      ]);
      setKpis(k.data); setBoard(b.data.warehouses || []); setDiscs(d.data.discrepancies || []);
    } catch { /* silent */ }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-3 flex gap-3" data-testid="reconciliation-tab">
      <div className="flex-1 min-w-0 space-y-3">
        {/* 6 KPI tiles */}
        <div className="grid grid-cols-6 gap-2">
          <KpiTile testId="rc-kpi-last" label="Last Full Count"
            value={fmtDate(kpis.lastFullCount?.startedAt)} sub={kpis.lastFullCount?.status || '—'} color={C.primary} />
          <KpiTile testId="rc-kpi-today" label="Warehouses Reconciled Today"
            value={`${kpis.warehousesReconciledToday || 0} / ${kpis.totalWarehouses || 0}`} color={C.success} />
          <KpiTile testId="rc-kpi-open" label="Open Discrepancies"
            value={kpis.openDiscrepancies || 0} sub={`$ ${fmt(kpis.openDiscrepancyValue)}`} color={C.warn} />
          <KpiTile testId="rc-kpi-pilf" label="Pilferage This Month"
            value={kpis.pilferageThisMonth || 0} color={C.danger} />
          <KpiTile testId="rc-kpi-waste" label="Wastage This Month"
            value={kpis.wastageThisMonth || 0} color={C.warn} />
          <KpiTile testId="rc-kpi-acc" label="Inventory Accuracy"
            value={`${kpis.accuracyScore ?? 100}%`} sub="Target ≥ 98%"
            color={(kpis.accuracyScore ?? 100) >= 98 ? C.success : C.danger} />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => { load(); setRefreshKey(k => k + 1); }}
            data-testid="rc-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="text-[10px]"
            onClick={async () => {
              try {
                const { data } = await api.post(`/v1/projects/${projectId}/mtx/rebalance-discipline-racks`);
                if (data.moved > 0) toast.success(`Rebalanced ${data.moved} unit(s) to correct discipline rack`);
                else toast.info('All units already in the right rack');
                if ((data.unmatched || []).length > 0) toast.warning(`${data.unmatched.length} unit(s) have no matching station rack`);
                load(); setRefreshKey(k => k + 1);
              } catch { toast.error('Rebalance failed'); }
            }}
            data-testid="rc-rebalance-disc">
            <Wrench className="w-3 h-3 mr-1" /> Rebalance discipline racks
          </Button>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>
            Approvals route by value: <b>≤$1k Storekeeper</b> · ≤$10k WH Manager · else Project Director · <b>PILFERAGE auto-creates Incident</b>
          </div>
        </div>

        {/* Warehouse Reconciliation Board */}
        <div>
          <div className="text-[10px] uppercase font-bold mb-1" style={{ color: C.primary }}>Warehouse Reconciliation Board</div>
          <div className="grid grid-cols-3 gap-2">
            {board.map(w => (
              <button key={w.warehouseLocation} onClick={() => setOpenWorkspace(w.warehouseLocation)}
                className="text-left p-2 rounded border bg-white hover:border-blue-400"
                style={{ borderColor: C.border }}
                data-testid={`rc-wh-${w.warehouseLocation}`}>
                <div className="flex items-center gap-1 mb-0.5">
                  <Package className="w-3 h-3" style={{ color: C.primary }} />
                  <div className="text-[11px] font-bold" style={{ color: C.primary }}>{w.warehouseLocation}</div>
                </div>
                <div className="text-[9px]" style={{ color: C.slateMuted }}>
                  Last: {fmtDate(w.lastCount?.startedAt)} · {w.lastCount?.cadence || '—'} · Open Disc: <b style={{ color: w.openDiscrepancies > 0 ? C.danger : C.success }}>{w.openDiscrepancies}</b>
                </div>
                <div className="text-[9px] font-mono mt-0.5" style={{ color: w.accuracyScore >= 98 ? C.success : C.warn }}>
                  {w.accuracyScore}% accuracy
                </div>
              </button>
            ))}
            {board.length === 0 && <div className="p-3 text-[10px]" style={{ color: C.slateMuted }}>No warehouses.</div>}
          </div>
        </div>

        {/* Discrepancy log */}
        <div className="rounded-lg border" style={{ borderColor: C.border }}>
          <div className="px-2 py-1.5 border-b flex items-center gap-1.5" style={{ borderColor: C.border }}>
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.danger }} />
            <div className="text-[10px] uppercase font-bold" style={{ color: C.danger }}>Discrepancy Log</div>
            <span className="ml-auto text-[10px] font-mono">{discs.length} total</span>
          </div>
          <table className="w-full text-[11px]">
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                {['MCI', 'Unit', 'Expected', 'Counted', 'Variance', 'Value Impact', 'Category', 'Approver', 'Status', ''].map(h => (
                  <th key={h} className="text-left p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {discs.length === 0 && <tr><td colSpan={10} className="p-4 text-center text-[10px]" style={{ color: C.slateMuted }}>No discrepancies yet.</td></tr>}
              {discs.map(d => <DiscrepancyRow key={d.id} projectId={projectId} disc={d}
                onChanged={() => { load(); setRefreshKey(k => k + 1); }}
                onOpenCase={(disc, kind) => setCaseModal({ disc, kind })} />)}
            </tbody>
          </table>
        </div>

        {/* Insurance & Theft cases panel — cognara-drafted */}
        <CasesPanel projectId={projectId} refreshKey={refreshKey}
          onOpenCase={(caseDoc, kind) => setCaseModal({
            disc: {
              id: caseDoc.sourceDiscrepancyId,
              mciLineNumber: caseDoc.mciLineNumber,
              materialUnitId: caseDoc.materialUnitId,
              valueImpact: caseDoc.valueImpact,
              currency: caseDoc.currency,
              category: caseDoc.sourceCategory,
            },
            kind, existingCase: caseDoc,
          })} />
      </div>

      <ReturnReconciliationPanel projectId={projectId} refreshKey={refreshKey}
        onChanged={() => { load(); setRefreshKey(k => k + 1); }} />
      <CognaraPanel projectId={projectId} refreshKey={refreshKey} />

      {openWorkspace && <WorkspaceDialog projectId={projectId} warehouseLocation={openWorkspace}
        onClose={() => setOpenWorkspace(null)}
        onSaved={() => { load(); setRefreshKey(k => k + 1); }} />}
      {caseModal && <CaseDraftModal projectId={projectId} disc={caseModal.disc} kind={caseModal.kind}
        existingCase={caseModal.existingCase}
        onClose={() => setCaseModal(null)}
        onSaved={() => { load(); setRefreshKey(k => k + 1); }} />}
    </div>
  );
}

function ReturnReconciliationPanel({ projectId, refreshKey, onChanged }) {
  const [data, setData] = useState({ pairs: [], counts: {} });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/v1/projects/${projectId}/mtx/return-reconciliation`);
      setData(d || { pairs: [], counts: {} });
    } catch { setData({ pairs: [], counts: {} }); }
    setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const ready = (data.pairs || []).filter(p => p.readyToClose);
  const inTransit = (data.pairs || []).filter(p => !p.whEntry);
  const closed = (data.pairs || []).filter(p => p.reconciled);
  const toggle = (uid) => {
    const n = new Set(selected);
    n.has(uid) ? n.delete(uid) : n.add(uid);
    setSelected(n);
  };
  const closeSelected = async () => {
    if (selected.size === 0) return toast.error('Pick at least one return leg');
    setBusy(true);
    try {
      const { data: r } = await api.post(
        `/v1/projects/${projectId}/mtx/return-reconciliation/close`,
        { materialUnitIds: Array.from(selected) });
      toast.success(`Closed ${r.closed} return leg(s)`);
      setSelected(new Set()); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-[420px] rounded-lg border bg-white p-3" style={{ borderColor: C.border }}
      data-testid="return-recon-panel">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4" style={{ color: C.accent }} />
        <div className="text-sm font-bold" style={{ color: C.slate }}>Return Reconciliation</div>
        <button onClick={load} className="ml-auto" data-testid="return-recon-refresh">
          <RefreshCw className="w-3.5 h-3.5" style={{ color: C.slateMuted }} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {[
          { k: 'ready', label: 'Ready', v: ready.length, color: C.success },
          { k: 'transit', label: 'In Transit', v: inTransit.length, color: C.warn },
          { k: 'closed', label: 'Closed', v: closed.length, color: C.slateMuted },
        ].map(t => (
          <div key={t.k} className="p-1.5 rounded border text-center"
            data-testid={`return-recon-kpi-${t.k}`}
            style={{ borderColor: `${t.color}44`, background: `${t.color}12` }}>
            <div className="text-[8.5px] uppercase font-semibold" style={{ color: t.color }}>{t.label}</div>
            <div className="text-base font-bold font-mono" style={{ color: t.color }}>{t.v}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <button onClick={() => setSelected(new Set(ready.map(p => p.materialUnitId)))}
          disabled={ready.length === 0}
          className="text-[10px] px-1.5 py-0.5 rounded font-semibold disabled:opacity-40"
          style={{ background: `${C.success}22`, color: C.success }}
          data-testid="return-recon-select-all">
          Select all Ready
        </button>
        <button onClick={closeSelected} disabled={busy || selected.size === 0}
          className="text-[10px] px-2 py-0.5 rounded font-bold disabled:opacity-40 flex items-center gap-1"
          style={{ background: C.accent, color: '#fff' }}
          data-testid="return-recon-close-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Close {selected.size} leg(s)
        </button>
      </div>
      <div className="rounded border overflow-y-auto" style={{ borderColor: C.border, maxHeight: 360 }}>
        {loading && <div className="p-3 text-[10px] italic text-center" style={{ color: C.slateMuted }}>Loading…</div>}
        {!loading && (data.pairs || []).length === 0 && (
          <div className="p-3 text-[10px] italic text-center" style={{ color: C.slateMuted }}>
            No station-exit scans yet. Return legs appear once a unit is scanned at a STATION_EXIT_GATE.
          </div>
        )}
        {!loading && (data.pairs || []).map(p => (
          <div key={p.materialUnitId}
            className="border-b px-2 py-1.5 flex items-start gap-2"
            style={{ borderColor: C.border,
                      background: p.reconciled ? '#F1F5F9' :
                                    p.readyToClose ? '#F0FDF4' : '#FFFBEB' }}
            data-testid={`return-recon-row-${p.materialUnitId}`}>
            {!p.reconciled && p.readyToClose && (
              <input type="checkbox" checked={selected.has(p.materialUnitId)}
                onChange={() => toggle(p.materialUnitId)}
                className="mt-0.5" data-testid={`return-recon-check-${p.materialUnitId}`} />
            )}
            {p.reconciled && <Check className="w-3 h-3 mt-0.5" style={{ color: C.slateMuted }} />}
            {!p.readyToClose && !p.reconciled && <AlertTriangle className="w-3 h-3 mt-0.5" style={{ color: C.warn }} />}
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-bold" style={{ color: C.slate }}>
                {p.materialCode || p.materialUnitId.slice(-10)}
                <span className="ml-1 text-[8.5px] font-mono uppercase"
                  style={{ color: p.reconciled ? C.slateMuted :
                                    p.readyToClose ? C.success : C.warn }}>
                  {p.reconciled ? 'CLOSED' : p.readyToClose ? 'READY' : 'IN TRANSIT'}
                </span>
              </div>
              <div className="text-[9px] font-mono truncate" style={{ color: C.slateMuted }}>
                MCI: {p.mciLineNumber || '—'} · Qty {p.quantity} · {p.currentBin || '—'}
              </div>
              <div className="text-[8.5px] mt-0.5" style={{ color: C.slateMuted }}>
                Exit: {p.stationExit?.scannedAt?.slice(0, 16) || '—'}
                {p.whEntry && <> · Entry: {p.whEntry.scannedAt.slice(0, 16)}</>}
                {p.mrn && <> · MRN {p.mrn.code}</>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
